/* ================================================================
   Legacy Adapter - maps old crate model JSON ↔ new ProjectModel.
   Ensures round-trip compatibility with existing saved data.
   ================================================================ */

import type {
  ProjectModel, Side, Layer, SideId, LayerStyle,
  StyleSetting, SizeGroups, OpeningConfig, Dimensions3,
} from '../model/types';
import { LEGACY_SIDE_NAME, LEGACY_SIDE_INDEX, SIDE_PAIR, SIDE_ID_LIST } from '../model/types';
import {
  createDefaultProject, createDefaultLayer, uid,
  defaultStyleSetting, dim3, vec3,
} from '../model/defaults';

// --------------- Legacy types (what old JSON looks like) ---------------

interface LegacyCrate {
  size?: {
    outter?: { length?: number; width?: number; height?: number };
    inner?: { length?: number; width?: number; height?: number };
    goods?: { length?: number; width?: number; height?: number };
  };
  logics?: {
    SideSymmetry?: number;
    EndSymmetry?: number;
    drawInitStatus?: boolean;
    [k: string]: unknown;
  };
  other?: {
    system?: string;
    style?: number;
    [k: string]: unknown;
  };
  [k: string]: unknown;
}

interface LegacyModel {
  crate?: { crate?: LegacyCrate; [k: string]: unknown };
  sides?: LegacySide[];
  [k: string]: unknown;
}

interface LegacySide {
  layers?: LegacyLayer[];
  adjustParam?: Record<string, unknown>;
  [k: string]: unknown;
}

interface LegacyLayer {
  size?: {
    board?: unknown[];
    frame?: unknown[][];
    horBrace?: unknown[][];
  };
  styleSetting?: {
    style?: number;
    material?: number;
    materialcolor?: string;
    CornerPack?: {
      corner?: boolean;
      pack?: { num?: number; space?: number };
    };
    logics?: {
      position?: { bottom?: number; toppos?: number; sidepos?: number; endpos?: number };
      size?: { frame?: number; horBrace?: number; board?: number; bevelBrace?: number };
      space?: { frame?: number; horBrace?: number; board?: number };
      count?: { horBrace?: number; bevelBrace?: number };
    };
    directions?: {
      mountain?: number;
      frame?: number;
      isHor?: number;
      horBrace?: number;
      toward?: number[];
    };
    count?: {
      frame?: number[];
      board?: number;
      horBrace?: number[];
      bevelBrace?: number[];
    };
  };
  kaicao?: {
    depth?: number;
    margins?: number;
    radius?: number;
    span?: number;
    space?: number;
  };
  isbase?: boolean;
  m_recordsno?: number;
  layer?: number;
  [k: string]: unknown;
}

// --------------- Import: Legacy → ProjectModel ---------------

export function importLegacyModel(json: string | LegacyModel): ProjectModel {
  const raw: LegacyModel = typeof json === 'string' ? JSON.parse(json) : json;
  const crate = raw.crate?.crate ?? ({} as LegacyCrate);

  const mode = detectMode(crate.other?.system);
  const project = createDefaultProject(mode);

  // Dimensions
  if (crate.size?.inner) {
    project.innerSize = dimFromLegacy(crate.size.inner);
  }
  if (crate.size?.outter) {
    project.outerSize = dimFromLegacy(crate.size.outter);
  }
  if (crate.size?.goods) {
    project.goodsSize = dimFromLegacy(crate.size.goods);
  }

  // Symmetry
  if (crate.logics) {
    project.symmetry.side = (crate.logics.SideSymmetry ?? 1) === 1;
    project.symmetry.end = (crate.logics.EndSymmetry ?? 1) === 1;
  }

  // Preserve legacy fields
  project._legacy = {
    crate: stripFunctions(crate),
    other: crate.other,
    logics: crate.logics,
  };

  // Sides
  if (raw.sides && Array.isArray(raw.sides)) {
    for (let si = 0; si < Math.min(raw.sides.length, 6); si++) {
      const legacySide = raw.sides[si];
      const sideId = LEGACY_SIDE_INDEX[si] ?? SIDE_ID_LIST[si];
      project.sides[si] = importLegacySide(legacySide, sideId, si);
    }
  }

  return project;
}

function importLegacySide(ls: LegacySide, sideId: SideId, sideIndex: number): Side {
  const side: Side = {
    sideId,
    pairSideId: SIDE_PAIR[sideId],
    enabled: true,
    layers: [],
    _legacyAdjustParam: ls.adjustParam,
  };

  if (ls.layers && Array.isArray(ls.layers)) {
    for (let li = 0; li < ls.layers.length; li++) {
      side.layers.push(importLegacyLayer(ls.layers[li], sideId, sideIndex, li));
    }
  }

  if (side.layers.length === 0) {
    side.layers.push(createDefaultLayer(sideId, 0));
  }

  return side;
}

function importLegacyLayer(
  ll: LegacyLayer,
  sideId: SideId,
  sideIndex: number,
  layerIndex: number,
): Layer {
  const ss = ll.styleSetting ?? {};
  const style = (ss.style ?? 0) as LayerStyle;
  const dir = ((ss.directions?.isHor ?? 0) === 1 ? 1 : 0) as 0 | 1;

  const layer = createDefaultLayer(sideId, layerIndex, style, dir);

  // Override styleSetting with legacy data
  if (ss.material !== undefined) {
    layer.materialKey = ss.material;
    layer.styleSetting.material = ss.material;
  }
  if (ss.materialcolor !== undefined) {
    layer.color = ss.materialcolor;
    layer.styleSetting.materialcolor = ss.materialcolor;
  }
  if (ss.CornerPack) {
    layer.styleSetting.CornerPack = {
      corner: ss.CornerPack.corner ?? false,
      pack: {
        num: ss.CornerPack.pack?.num ?? 0,
        space: ss.CornerPack.pack?.space ?? 50,
      },
    };
  }
  if (ss.logics) {
    if (ss.logics.position) {
      Object.assign(layer.styleSetting.logics.position, ss.logics.position);
    }
    if (ss.logics.size) {
      Object.assign(layer.styleSetting.logics.size, ss.logics.size);
    }
    if (ss.logics.space) {
      Object.assign(layer.styleSetting.logics.space, ss.logics.space);
    }
    if (ss.logics.count) {
      Object.assign(layer.styleSetting.logics.count, ss.logics.count);
    }
  }
  if (ss.directions) {
    layer.styleSetting.directions = {
      mountain: ss.directions.mountain ?? 0,
      frame: ss.directions.frame ?? dir,
      isHor: dir,
      horBrace: ss.directions.horBrace ?? (dir === 0 ? 1 : 0),
      toward: ss.directions.toward ?? [],
    };
  }
  if (ss.count) {
    if (ss.count.frame) layer.styleSetting.count.frame = ss.count.frame;
    if (ss.count.board !== undefined) layer.styleSetting.count.board = ss.count.board;
    if (ss.count.horBrace) layer.styleSetting.count.horBrace = ss.count.horBrace;
    if (ss.count.bevelBrace) layer.styleSetting.count.bevelBrace = ss.count.bevelBrace;
  }

  // BlockBoard: migrate legacy style=4 frame counts to blockBoard config
  if (style === 4) {
    const legacyFrames = ll.size?.frame as Array<Array<{ length?: number; width?: number }>> | undefined;
    const firstBlock = legacyFrames?.[0]?.[0];
    const xCount = layer.styleSetting.count.frame[0] || 3;
    const zCount = layer.styleSetting.count.frame[1] || 3;
    layer.blockBoard = {
      boardLength: Number(firstBlock?.length || 90),
      boardWidth: Number(firstBlock?.width || 90),
      count: xCount,
      centerSpacing: 0,
    };
    layer.styleSetting.count.frame = [xCount, zCount];
  }

  // Openings (kaicao)
  if (ll.kaicao) {
    layer.openings = {
      depth: ll.kaicao.depth ?? 0,
      margins: ll.kaicao.margins ?? 0,
      radius: ll.kaicao.radius ?? 0,
      span: ll.kaicao.span ?? 0,
      space: ll.kaicao.space ?? 0,
    };
  }

  layer.isBase = ll.isbase ?? (layerIndex === 0);
  layer.recordsNo = ll.m_recordsno ?? (sideIndex * 10 + layerIndex);

  // Store all unknown legacy fields
  const knownKeys = new Set([
    'size', 'styleSetting', 'kaicao', 'isbase', 'm_recordsno', 'layer',
  ]);
  const legacy: Record<string, unknown> = {};
  for (const key of Object.keys(ll)) {
    if (!knownKeys.has(key)) {
      legacy[key] = ll[key];
    }
  }
  if (Object.keys(legacy).length > 0) {
    layer._legacy = legacy;
  }

  return layer;
}

// --------------- Export: ProjectModel → Legacy JSON ---------------

export function exportToLegacyModel(project: ProjectModel): LegacyModel {
  const crate: LegacyCrate = {
    size: {
      outter: { ...project.outerSize },
      inner: { ...project.innerSize },
      goods: { ...project.goodsSize },
    },
    logics: {
      SideSymmetry: project.symmetry.side ? 1 : 0,
      EndSymmetry: project.symmetry.end ? 1 : 0,
      drawInitStatus: true,
      ...(project._legacy?.logics as Record<string, unknown> ?? {}),
    },
    other: {
      system: project.meta.mode === 'pallet' ? 'Pallet' : 'Crate',
      style: 0,
      ...(project._legacy?.other as Record<string, unknown> ?? {}),
    },
  };

  const sides: LegacySide[] = project.sides.map((side, si) => {
    const legacySide: LegacySide = {
      layers: side.layers.map((layer, li) => exportLayerToLegacy(layer, si, li)),
      adjustParam: side._legacyAdjustParam,
    };
    return legacySide;
  });

  return {
    crate: { crate },
    sides,
  };
}

function exportLayerToLegacy(layer: Layer, sideIndex: number, layerIndex: number): LegacyLayer {
  const result: LegacyLayer = {
    size: {
      board: layer.sizeGroups.board,
      frame: layer.sizeGroups.frame,
      horBrace: layer.sizeGroups.horBrace,
    },
    styleSetting: JSON.parse(JSON.stringify(layer.styleSetting)),
    kaicao: { ...layer.openings },
    isbase: layer.isBase,
    m_recordsno: sideIndex * 10 + layerIndex,
    layer: layerIndex,
    ...layer._legacy,
  };
  return result;
}

// --------------- Utilities ---------------

function detectMode(system?: string): 'crate' | 'pallet' {
  if (!system) return 'crate';
  return system.toLowerCase() === 'pallet' ? 'pallet' : 'crate';
}

function dimFromLegacy(d: { length?: number; width?: number; height?: number }): Dimensions3 {
  return {
    length: Number(d.length ?? 0),
    width: Number(d.width ?? 0),
    height: Number(d.height ?? 0),
  };
}

function stripFunctions(obj: unknown): unknown {
  return JSON.parse(JSON.stringify(obj));
}
